import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { FiArrowLeft, FiDownload } from "react-icons/fi";
import style from "../monetization/Monetization.module.css";
import { showToast } from "../../utils/Toastify/Toastify";
import { getMyCreatorContentDetail } from "../../api/HttpApis";

const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const hrs = (m) => {
  const n = Number(m) || 0;
  const h = Math.floor(n / 60);
  const r = n % 60;
  return h > 0 ? `${h}h ${r}m` : `${r}m`;
};
const body = (res) => res?.data ?? res ?? {};
const TT = { background: "#2a2a2a", border: "1px solid #444", color: "#fff", borderRadius: 8 };

export default function MovieMonetization() {
  const { videoMetaId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getMyCreatorContentDetail(videoMetaId);
        if (active) setData(body(res));
      } catch (e) {
        showToast(e?.response?.data?.message || "Could not load content.", "error");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [videoMetaId]);

  if (loading) return <div className={style.page}><div className={style.loading}>Loading…</div></div>;
  if (!data?.content) return <div className={style.page}><div className={style.empty}>Content not found.</div></div>;

  const c = data.content;
  const cards = data.cards || {};
  const perf = data.performance || [];

  return (
    <div className={style.page}>
      <div className={style.headerRow}>
        <button className={`${style.btn} ${style.btnGhost}`} onClick={() => navigate(-1)}>
          <FiArrowLeft style={{ marginRight: 6 }} /> Back
        </button>
      </div>

      <div className={style.profileCard} style={{ alignItems: "flex-start" }}>
        {c.thumbnail ? (
          <img src={c.thumbnail} alt={c.title} style={{ width: 150, height: 86, objectFit: "cover", borderRadius: 10 }} />
        ) : (
          <div style={{ width: 150, height: 86, borderRadius: 10, background: "#333" }} />
        )}
        <div>
          <h2 className={style.profileName} style={{ fontSize: 24 }}>{c.title}</h2>
          <div className={style.rowActions} style={{ marginTop: 8 }}>
            {c.access && <span className={style.chip} style={{ border: "1px solid #b87333", color: "#b87333" }}>{c.access}</span>}
            {c.status && <span className={style.chip} style={{ border: "1px solid rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.7)" }}>{c.status}</span>}
          </div>
        </div>
      </div>

      <div className={style.cardGrid}>
        <div className={style.card}>
          <div className={style.cardLabel}>Watch Time</div>
          <div className={style.cardValue}>{hrs(cards.total_watch_minutes)}</div>
          <div className={style.cardHint}>Lifetime</div>
        </div>
        <div className={style.card}>
          <div className={style.cardLabel}>Watch Time Revenue</div>
          <div className={style.cardValue}>{money(cards.watch_time_revenue_cents)}</div>
          <div className={style.cardHint}>Estimate — lifetime watch time at this month's rate</div>
        </div>
        <div className={style.card}>
          <div className={style.cardLabel}>Total Views</div>
          <div className={style.cardValue}>{(cards.total_views || 0).toLocaleString()}</div>
          <div className={style.cardHint}>Lifetime</div>
        </div>
      </div>

      <div className={style.chartCard}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h3 className={style.chartTitle}>Performance Across Platform</h3>
            <p className={style.chartSub}>How this video performed over the last 4 weeks</p>
          </div>
          <button className={`${style.btn} ${style.btnGhost}`} onClick={() => showToast("Report coming soon", "info")}>
            <FiDownload style={{ marginRight: 6 }} /> Download Report
          </button>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={perf}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
            <XAxis dataKey="label" stroke="#fff" />
            <YAxis stroke="#fff" />
            <Tooltip contentStyle={TT} formatter={(v) => `${v} hrs`} />
            <Line type="monotone" dataKey="hours" stroke="#b87333" strokeWidth={2} dot={{ fill: "#b87333", r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
